import React, { Component, useEffect, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import NonCritical from '@atlassian/jira-non-critical/src/NonCriticalComponent.tsx';
import { DRAWER } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	ProjectCreateMethods,
	ProjectCreateData,
} from '@atlassian/jira-project-create-drawer-context/src/types.tsx';
import { PROJECT_CREATE_LOAD_START_MARK_KEY } from '@atlassian/jira-project-create-main/src/common/constants.tsx';
import type { ViewProps, ViewType, Permissions, PermissionsFetched } from './types.tsx';
import { AsyncProjectCreateDrawerContent, preloadAsyncProjectCreateDrawer } from './view/async.tsx';

const PreloadAsyncProjectCreateDrawer = () => {
	useEffect(() => {
		preloadAsyncProjectCreateDrawer();
	}, []);
	return null;
};

// eslint-disable-next-line jira/react/no-class-components
const View: ViewType = class ProjectCreateDrawer extends Component<ViewProps> {
	static defaultProps = {
		isAdmin: false,
		isOnboarding: false,
	};

	onScreenChange = (screen: string) => {
		this.screen = screen;
	};

	onPermissionsFetched = ({
		canCreateClassicProject,
		canCreateNextGenProject,
	}: PermissionsFetched) => {
		this.permissions = {
			canCreateClassicProject,
			canCreateNextGenProject,
		};
	};

	screen = '';

	permissions: Permissions = {
		canCreateClassicProject: null,
		canCreateNextGenProject: null,
	};

	renderContent = (data: ProjectCreateData, methods: ProjectCreateMethods) => {
		const { isAdmin, locale, isOnboarding, appEditions, ProjectCreate } = this.props;

		if (data.isOpen) {
			setMark(PROJECT_CREATE_LOAD_START_MARK_KEY);
		}

		const onClose = (e: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			methods.close();
			this.props.onClose &&
				this.props.onClose(
					{
						screen: this.screen,
						isAdmin: this.props.isAdmin,
						canCreateClassicProject: this.permissions.canCreateClassicProject,
						canCreateNextGenProject: this.permissions.canCreateNextGenProject,
					},
					analyticsEvent,
				);
		};

		return (
			<AsyncProjectCreateDrawerContent
				onClose={onClose}
				isAdmin={isAdmin}
				isOnboarding={
					fg('jira_project_create_drawer_support_onboarding')
						? data.isOnboarding || isOnboarding
						: isOnboarding
				}
				data={data}
				methods={methods}
				locale={locale}
				appEditions={appEditions}
				onScreenChange={this.onScreenChange}
				onPermissionsFetched={this.onPermissionsFetched}
				ProjectCreate={ProjectCreate}
			/>
		);
	};

	render() {
		const { Consumer } = this.props;

		return (
			<>
				{Consumer ? (
					<Consumer>
						{({ data, methods }) =>
							data.shouldRenderDrawer ? this.renderContent(data, methods) : null
						}
					</Consumer>
				) : null}
				<NonCritical>
					<PreloadAsyncProjectCreateDrawer />
				</NonCritical>
			</>
		);
	}
};

export default AnalyticsSource('ProjectCreate', DRAWER)(View);
